import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { detalleSolicitud, detalleSolicitudTodos } from "../Services/Rest";
import Cookies from "universal-cookie";
import { useTable, usePagination, useSortBy } from "react-table";
import CambiaEstado from "./Evaluador/CambiaEstado";
import Swal from "sweetalert2";
import InformesRepositorio from "./ObtenerInforme";
import CrearSolicitud from "./Modals/crear/CrearSolicitud";
import Avance from "./Evaluador/Avance";
import { Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const cookies = new Cookies();

function Solicitudes({ correoDesencriptado }) {
  const [usuarioId, setUsuarioId] = useState(null);
  const [solicitud, setSolicitud] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Cargando...");

  const basicAuthHeader =
    "Basic " + btoa("BasicAuth@icontec.org:yQLzqmR7eZSuAmQZUqhY");

  // 🔎 Obtener info del usuario desde el correo
  useEffect(() => {
    if (!correoDesencriptado) return;

    const obtenerUsuario = async () => {
      try {
        const response = await fetch(
          `https://suadesarrollo.azurewebsites.net/api/obtenerIdUsuario?correo=${correoDesencriptado}`,
          { headers: { Authorization: basicAuthHeader } },
        );

        if (!response.ok) throw new Error("Error HTTP");
        const data = await response.json();

        // 🧠 Verificamos si ya hay cookies o es el primer ingreso
        if (!cookies.get("usuarioId")) {
          Swal.fire({
            title: `👋 Bienvenido ${data.nombre}`,
            text: "Estamos cargando tu información",
            icon: "success",
            confirmButtonText: "Continuar",
          }).then(() => {
            // ✅ Guardar en cookies al confirmar
            cookies.set("usuarioId", data.id, { path: "/" });
            cookies.set("id", data.id, { path: "/" });

            cookies.set("Nombre", data.nombre, { path: "/" });
            cookies.set("correo", data.correo_Electronico, { path: "/" });
            cookies.set("tipo_Usuario", data.tipo_Usuario, { path: "/" });
            cookies.set("institucionid", data.institucionId, { path: "/" });

            setUsuarioId(data.id);
            setStatus("✅ Usuario cargado correctamente");
            console.log("✅ Datos guardados en cookies:", data);
          });
        } else {
          // Si las cookies ya existen, solo continuar
          setUsuarioId(cookies.get("usuarioId"));
          setStatus("🔄 Cookies cargadas automáticamente");
        }
      } catch (error) {
        console.error("Error al obtener usuario:", error);
        setStatus("❌ Error al obtener usuario");
        Swal.fire("Error", "No se pudo obtener el usuario", "error");
      }
    };

    obtenerUsuario();
  }, [correoDesencriptado]);

  // 📌 Definir endpoint según tipo de usuario
  const urlendpoint = useMemo(() => {
    const tipoUsuario = cookies.get("tipo_Usuario");
    const institucionId = cookies.get("institucionid");
    return tipoUsuario == 1 || tipoUsuario == 4
      ? detalleSolicitudTodos
      : `${detalleSolicitud}${institucionId}`;
  }, [cookies.get("tipo_Usuario"), cookies.get("institucionid")]);

  // 🔄 Obtener solicitudes
  useEffect(() => {
    if (usuarioId !== null) {
      showData();
    }
  }, [usuarioId, urlendpoint]);

  const showData = async () => {
    try {
      const response = await fetch(urlendpoint, {
        headers: { Authorization: basicAuthHeader },
      });

      const data = await response.json();
      console.log("📄 Datos API:", data);

      if (Array.isArray(data)) {
        setSolicitud(data);
      } else if (Array.isArray(data.resultado)) {
        setSolicitud(data.resultado);
      } else {
        setSolicitud([]);
      }
    } catch (error) {
      console.error("Error al obtener datos:", error);
      setSolicitud([]);
    }
  };

  // 🔍 Buscador
  const searcher = (e) => setSearch(e.target.value);

  // 🔄 Filtrar datos
  const results = useMemo(() => {
    if (!Array.isArray(solicitud)) return [];
    return !search
      ? solicitud
      : solicitud.filter((dato) =>
          dato.nombreInstitucion?.toLowerCase().includes(search.toLowerCase()),
        );
  }, [search, solicitud]);

  // 📌 Columnas de tabla
  const tipoUsuario = cookies.get("tipo_Usuario");

  const columns = useMemo(() => {
    let baseColumns = [
      { Header: "N° Solicitud", accessor: "solicitud" },
      { Header: "Nombre", accessor: "nombreInstitucion" },
      { Header: "Nit", accessor: "nitInstitucion" },
      { Header: "Estado", accessor: "estado" },
      { Header: "Tipo Solicitud", accessor: "tipoSolicitud" },

      {
        id: "proceso_solicitud",

        Header: () => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              whiteSpace: "nowrap",
            }}
          >
            <span>Proceso de Solicitud</span>

            <Tooltip
              title="Solo cuando la solicitud se encuentre en estado En diligenciamiento podrá realizar modificaciones."
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                sx={{
                  padding: 0,
                  color: "#0085CA",
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),

        Cell: ({ row }) => {
          const estado = row.original.estado?.trim().toLowerCase();
          const tipoUsuario = Number(cookies.get("tipo_Usuario"));

          // Usuario tipo 1 puede acceder siempre
          const puedeAcceder =
            tipoUsuario === 1 || estado === "en diligenciamiento";

          return puedeAcceder ? (
            <Link
              to={`/ProcesoSolicitud/${row.original.solicitud}/${cookies.get(
                "institucionid",
              )}`}
            >
              Solicitudes
            </Link>
          ) : (
            <span className="text-muted">No disponible</span>
          );
        },
      },

      {
        id: "proceso_autoevaluacion",
        Header: () => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              whiteSpace: "nowrap",
            }}
          >
            <span>Proceso de Autoevaluación</span>

            <Tooltip
              title="Solo cuando la solicitud se encuentre en estado En diligenciamiento podrá realizar modificaciones."
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                sx={{
                  padding: 0,
                  color: "#0085CA",
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),

        Cell: ({ row }) => {
          const estado = row.original.estado?.trim().toLowerCase();

          return estado === "en diligenciamiento" ? (
            <Link
              to={`/ProcesoAutoevaluacion/${row.original.solicitud}/${row.original.idinstitucion}/${row.original.idTipoSolicitud}`}
            >
              Autoevaluación
            </Link>
          ) : (
            <span className="text-muted">No disponible</span>
          );
        },
      },

      {
        Header: "Informe Autoevaluación",
        Cell: ({ row }) => (
          <Link
            to={`/InformeAuto/${row.original.idinstitucion}/${row.original.solicitud}`}
          >
            Informe Autoevaluación
          </Link>
        ),
      },

      // ✅ AHORA TODOS VEN ESTO
      {
        Header: "Informes PDF",
        Cell: ({ row }) => {
          const estado = row.original.estado?.toLowerCase();

          const estadosBloqueados = [
            "cancelada",
            "en diligenciamiento",
            "en revision",
            "en revisión",
          ];

          const mostrarBtn = !estadosBloqueados.includes(estado);

          return mostrarBtn ? (
            <InformesRepositorio
              idSolicitud={row.original.solicitud}
              idinstitucion={row.original.idinstitucion}
            />
          ) : null;
        },
      },
    ];

    // 🔥 SOLO tipoUsuario == 1
    if (tipoUsuario == 1) {
      baseColumns.push(
        {
          Header: "Equipo Evaluador",
          Cell: ({ row }) => (
            <Link to={`/EquipoEvaluador/${row.original.solicitud}`}>
              Equipo Evaluador
            </Link>
          ),
        },
        {
          Header: "Informe Evaluador",
          Cell: ({ row }) => (
            <Link
              to={`/InformeEvaluador/${row.original.idinstitucion}/${row.original.solicitud}`}
            >
              Informe Evaluador
            </Link>
          ),
        },
        {
          Header: "Cambio estado",
          Cell: ({ row }) => (
            <CambiaEstado
              idSolicitud={row.original.solicitud}
              idinstitucion={row.original.idinstitucion}
              estadoActualId={row.original.tipoEstadoId}
            />
          ),
        },
        {
          Header: "Avance Evaluador",
          Cell: ({ row }) => <Avance idSolicitud={row.original.solicitud} />,
        },
        {
          Header: "Equipo Junta",
          Cell: ({ row }) => (
            <Link to={`/EquipoJunta/${row.original.solicitud}`}>
              Equipo de junta
            </Link>
          ),
        },
      );
    }

    return baseColumns;
  }, [tipoUsuario]);

  // 📊 Configuración de tabla
  const tableInstance = useTable(
    { columns, data: results, initialState: { pageSize: 5 } },
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex },
    pageOptions,
  } = tableInstance;

  return (
    <div className="card shadow mb-4">
      <div className="card-header">
        <h6 className="m-0 font-weight-bold text-primary">Solicitudes</h6>
      </div>
      <div className="card-body">
        <center>
          {cookies.get("tipo_Usuario") == 2 ? <CrearSolicitud /> : <h></h>}
        </center>
        <br></br>
        <input
          value={search}
          onChange={searcher}
          placeholder="Buscar por institución"
          className="form-control"
        />
        <div className="table-responsive">
          <table {...getTableProps()} className="table mt-3 table-bordered">
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                      {col.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-between">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="btn btn-secondary"
          >
            Anterior
          </button>
          <span>
            Página {pageIndex + 1} de {pageOptions.length}
          </span>
          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="btn btn-secondary"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}

export default Solicitudes;
